import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Wallet } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Income Sources — How He Actually Earns Money";
const DESC =
  "Raj Shamani earns from manufacturing, the Figuring Out podcast, brand partnerships, paid speaking and startup investments. Here is how each stream works.";
const URL = "https://rajshamani.life/raj-shamani-income";
const OG = OG_ABOUT;

const streams = [
  {
    name: "Shamani Industries",
    what: "Manufacturing",
    detail:
      "The family business in Indore, where Raj has been a director since his teens. Manufacturing is the oldest and most conventional part of his income — B2B revenue from polymers and additives, not content.",
    to: "/shamani-industries",
  },
  {
    name: "Figuring Out media",
    what: "Podcast & YouTube",
    detail:
      "Ad revenue, sponsorships and integrations across the Figuring Out podcast and his YouTube channel. Long-form business interviews attract sponsors who want a founder audience rather than a mass audience.",
    to: "/raj-shamani-podcast",
  },
  {
    name: "Brand partnerships",
    what: "Creator income",
    detail:
      "Paid collaborations on Instagram, YouTube and LinkedIn with finance, technology and consumer brands. This is the most visible stream and the one people usually mean when they ask how creators earn.",
    to: "/raj-shamani-instagram",
  },
  {
    name: "Paid speaking",
    what: "Stages & events",
    detail:
      "Keynotes and panels at conferences, corporate events and universities across India and abroad, including appearances at the United Nations. Speaking fees are negotiated per event and are not public.",
    to: "/contact",
  },
  {
    name: "Angel investing & House of X",
    what: "Equity upside",
    detail:
      "Early-stage cheques into Indian startups plus the House of X venture studio. This stream pays nothing month to month — it pays when a company is acquired or raises at a higher valuation.",
    to: "/house-of-x",
  },
  {
    name: "Build, Don't Talk",
    what: "Book royalties",
    detail:
      "Royalties from his bestselling book. Book income is small relative to the other streams for most Indian authors; the real return is credibility and audience.",
    to: "/build-dont-talk",
  },
];

const faq = [
  {
    q: "How does Raj Shamani earn money?",
    a: "Raj Shamani earns from six main streams: Shamani Industries (manufacturing), the Figuring Out podcast and media business, brand partnerships, paid speaking engagements, angel investments and House of X, and royalties from his book Build, Don't Talk.",
  },
  {
    q: "What is Raj Shamani's monthly income?",
    a: "No monthly income figure has been officially disclosed. Most of his income is business revenue and equity rather than a salary, so a single monthly number would not be meaningful. Figures circulating online are estimates, not disclosures.",
  },
  {
    q: "Does Raj Shamani earn mainly from YouTube?",
    a: "No. YouTube and podcast revenue is one stream among several. Manufacturing revenue, brand partnerships, speaking fees and startup equity all contribute, which is why his income is less volatile than a pure creator's.",
  },
  {
    q: "Is podcasting profitable in India?",
    a: "Ad revenue alone rarely is. Profitable Indian podcasts monetise through sponsorships, brand deals, events and the business opportunities the audience creates — which is the model Figuring Out follows.",
  },
  {
    q: "What is the difference between his income and his net worth?",
    a: "Income is what he earns in a period; net worth is the value of everything he owns minus what he owes, including illiquid startup equity. Neither has been officially published.",
  },
];

export const Route = createFileRoute("/raj-shamani-income")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani income sources", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: IncomePage,
});

function IncomePage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Income" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Wallet size={14} /> Income sources
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            How Raj Shamani <span className="text-gradient-gold">earns money</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani has never published an income figure. What is documented is the{" "}
            <strong className="text-foreground">structure</strong>: manufacturing revenue from
            Shamani Industries, the Figuring Out podcast and media business, brand partnerships,
            paid speaking, angel investments through House of X, and royalties from Build,
            Don&apos;t Talk. Each stream is broken down below.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Six income streams, explained</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {streams.map((s) => (
            <div key={s.name} className="p-6 rounded-xl border border-border bg-card">
              <div className="text-xs uppercase tracking-widest text-gold mb-2">{s.what}</div>
              <h3 className="font-bold mb-2">{s.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">{s.detail}</p>
              <Link to={s.to} className="text-sm text-gold underline underline-offset-4">
                {`Read more about ${s.name}`}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Why the numbers online disagree</h2>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          Most &ldquo;Raj Shamani income&rdquo; figures on the internet are reverse-engineered from
          YouTube view counts, which ignores manufacturing revenue and treats illiquid startup
          equity as cash. Because none of these numbers are disclosed, this page describes where the
          money comes from rather than inventing a figure. The same reasoning applies on the{" "}
          <Link to="/raj-shamani-net-worth" className="text-gold underline underline-offset-4">
            net worth page
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-income" />
      <RelatedPages pages={["aion", "ventures", "about", "faq", "podcast", "contact"]} />
    </div>
  );
}
